import { Injectable } from '@angular/core';
import { ResourceGroup } from '../types/monitoring/resource-group.type';
import { SchedulerProcess } from '../types/scheduler/scheduler-process.type';
import { SchedulerQueue } from '../types/scheduler/scheduler-queue.type';
import { SchedulerActionParameter } from '../types/scheduler/scheduler-task.type';
import { ApiService } from './api/api.service';
import { SchedulerResource } from '../types/scheduler/scheduler-resource.type';
import { SchedulerLoginUser } from '../types/scheduler/scheduler-login-user.type';
import { SchedulerLoginUserGroup } from '../types/scheduler/scheduler-login-user-group.type';
import { TenantsService } from './tenants.service';
import { BaseModel } from '../types/generated.types';

@Injectable({ providedIn: 'root' })
export class SchedulerCacheService {
  private processes: SchedulerProcess[] = [];
  private queues: SchedulerQueue[] = [];
  private resources: SchedulerResource[] = [];
  private resourceGroups: ResourceGroup[] = [];
  private loginUsers: SchedulerLoginUser[] = [];
  private loginUserGroups: SchedulerLoginUserGroup[] = [];
  private externalSystems: BaseModel[] = [];
  private loginProcessParameters = new Map<string, SchedulerActionParameter[]>();
  private logoutProcessParameters = new Map<string, SchedulerActionParameter[]>();
  private processesById = new Map<string, SchedulerProcess>();
  private queuesById = new Map<string, SchedulerQueue>();
  private resourcesById = new Map<string, SchedulerResource>();
  private resourceGroupsById = new Map<string, ResourceGroup>();
  private loginUsersById = new Map<string, SchedulerLoginUser>();
  private loginUserGroupsById = new Map<string, SchedulerLoginUserGroup>();

  constructor(private apiService: ApiService, private tenantsService: TenantsService) { }

  public getProcesses(tenantId?: string): SchedulerProcess[] {
    return tenantId == null ? this.processes.slice() : this.processes.filter(p => p.tenantId === tenantId);
  }

  public getQueues(tenantId: string): SchedulerQueue[] {
    return tenantId == null ? this.queues.slice() : this.queues.filter(q => q.tenantId === tenantId);
  }

  public getResources(tenantId: string): SchedulerResource[] {
    return tenantId == null ? this.resources.slice() :  this.resources.filter(r => r.tenantId === tenantId);
  }

  public getResourceGroups(tenantId?: string): ResourceGroup[] {
    return tenantId == null ? this.resourceGroups.slice() : this.resourceGroups.filter(r => r.tenantId === tenantId);
  }

  public getLoginUsers(tenantId: string): SchedulerLoginUser[] {
    return tenantId == null ? this.loginUsers.slice() : this.loginUsers.filter(l => l.tenantId === tenantId);
  }

  public getLoginUserGroups(tenantId?: string): SchedulerLoginUserGroup[] {
    return tenantId == null ? this.loginUserGroups.slice() : this.loginUserGroups.filter(l => l.tenantId === tenantId);
  }

  public getExternalSystems(): BaseModel[] {
    return this.externalSystems.slice();
  }

  public getProcess(processId: string): SchedulerProcess {
    return this.processesById?.get(processId);
  }

  public getProcessLabel(processId: string): string {
    if (!processId) {
      return null;
    }
    return this.processesById?.get(processId)?.label ?? processId;
  }

  public getProcessTenantLabel(processId: string, originalProcessId: string): string {
    const tenantId = this.processesById?.get(processId)?.tenantId;
    const originalProcess = this.processesById?.get(originalProcessId);
    if (!tenantId || tenantId === originalProcess?.tenantId) {
      return null;
    }
    const tenantLabel = this.tenantsService.getTenantLabel(tenantId);
    return tenantLabel;
  }

  public getQueueLabel(queueId: string): string {
    return this.queuesById?.get(queueId)?.label ?? queueId;
  }

  public getResourceGroupLabel(resourceGroupId: string): string {
    return this.resourceGroupsById?.get(resourceGroupId)?.label ?? resourceGroupId;
  }

  public getResource(resourceId: string): SchedulerResource {
    return this.resourcesById?.get(resourceId);
  }

  public getResourceLabel(resourceId: string): string {
    if (!resourceId) {
      return null;
    }
    return this.resourcesById?.get(resourceId)?.label ?? resourceId;
  }

  public getLoginUserLabel(loginUserId: string): string {
    if (!loginUserId) {
      return null;
    }
    return this.loginUsersById?.get(loginUserId)?.label ?? loginUserId;
  }

  public getLoginUserGroupLabel(loginUserGroupId: string): string {
    if (!loginUserGroupId) {
      return null;
    }
    return this.loginUserGroupsById?.get(loginUserGroupId)?.label ?? loginUserGroupId;
  }

  public getLoginProcessParameters(tenantId: string): SchedulerActionParameter[] {
    return this.loginProcessParameters.get(tenantId);
  }

  public getLogoutProcessParameters(tenantId: string): SchedulerActionParameter[] {
    return this.logoutProcessParameters.get(tenantId);
  }

  public getExternalSystemLabel(externalSystemId: string): string {
    return this.externalSystems?.find(es => es.id === externalSystemId)?.label ?? externalSystemId;
  }

  public async initialize(): Promise<void> {
    try {
      this.processes = await this.apiService.scheduler.getSchedulerProcesses();
      this.processesById = new Map(this.processes.map(p => [p.id, p]));
      await this.initializeQueues();
      this.resources = await this.apiService.scheduler.getSchedulerResources();
      this.resourcesById = new Map(this.resources.map(r => [r.id, r]));
      await this.initializeLoginUsers();
      await this.initializeLoginUserGroups();
      await this.initializeResourceGroups();
      await this.initializeLoginAndLogoutParameters();
      await this.initializeExternalSystems();
    } catch (error) {
      console.error('Failed to initialize SchedulerCacheService', error);
      this.processes = [];
      this.queues = [];
      this.resources = [];
      this.resourceGroups = [];
      this.loginUsers = [];
      this.loginUserGroups = [];
      this.externalSystems = [];
      this.loginProcessParameters = new Map();
      this.logoutProcessParameters = new Map();
      this.processesById = new Map();
      this.queuesById = new Map();
      this.resourcesById = new Map();
      this.resourceGroupsById = new Map();
      this.loginUsersById = new Map();
      this.loginUserGroupsById = new Map();
    }
  }

  public async initializeQueues(): Promise<void> {
    this.queues = await this.apiService.scheduler.getSchedulerQueues();
    this.queuesById = new Map(this.queues.map(q => [q.id, q]));
  }

  public async initializeResourceGroups(): Promise<void> {
    this.resourceGroups = await this.apiService.resourceGroups.getResourceGroups();
    this.resourceGroupsById = new Map(this.resourceGroups.map(r => [r.id, r]));
  }

  public async initializeLoginAndLogoutParameters(): Promise<void> {
    this.loginProcessParameters = new Map();
    this.logoutProcessParameters = new Map();
    const tenantsConfigurationUi = this.tenantsService.tenantConfigurations;
    for (const tenantConfigurationUi of tenantsConfigurationUi) {
      try {
        if (tenantConfigurationUi?.loginProcessId != null) {
          const schedulerProcessConfigurationForLogin = await this.apiService.scheduler.getSchedulerProcessConfiguration(tenantConfigurationUi.loginProcessId);
          this.loginProcessParameters.set(tenantConfigurationUi.tenantId, schedulerProcessConfigurationForLogin?.parameters ?? []);
        }
        if (tenantConfigurationUi?.logoutProcessId != null) {
          const schedulerProcessConfigurationForLogout = await this.apiService.scheduler.getSchedulerProcessConfiguration(tenantConfigurationUi.logoutProcessId);
          this.logoutProcessParameters.set(tenantConfigurationUi.tenantId, schedulerProcessConfigurationForLogout?.parameters ?? []);
        }
      } catch {
        // nothing to do
      }
    }
  }

  public async initializeLoginUsers(): Promise<void> {
    this.loginUsers = await this.apiService.schedulerLoginUsers.getLoginUsers();
    this.loginUsers.sort((a, b) => a.label.localeCompare(b.label));
    this.loginUsersById = new Map(this.loginUsers.map(l => [l.id, l]));
  }

  public async initializeLoginUserGroups(): Promise<void> {
    this.loginUserGroups = await this.apiService.schedulerLoginUsers.getLoginUserGroups();
    this.loginUserGroups.sort((a, b) => a.label.localeCompare(b.label));
    this.loginUserGroupsById = new Map(this.loginUserGroups.map(l => [l.id, l]));
  }

  public async initializeExternalSystems(): Promise<void> {
    this.externalSystems = await this.apiService.externalSystems.getExternalSystemsList();
    this.externalSystems.sort((a, b) => a.label.localeCompare(b.label));
  }
}
